import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

// Trees are authored in scene metres.  Their natural height is 40 ft, while
// their world anchor remains this application's invariant: z === 0.
export const NATURAL_TOTAL_HEIGHT = 12.192;
export const TREE_ARROW_OFFSET = 2.1;

const CANOPY_HEIGHT = 10.119;
const CANOPY_RADIUS = 4.884;
const TRUNK_RADIUS_TOP = 0.378;
const TRUNK_RADIUS_BOTTOM = 0.757;
const AUTHORED = {
  circle: { trunkHeight: 12.33, canopyYStretch: 1.0359 },
  cone: { trunkHeight: 13, canopyYStretch: 1 },
};

export function normalizeTreeShape(shape) {
  return shape === "cone" ? "cone" : "circle";
}

export function getTreeDimensions(shape) {
  const authored = AUTHORED[normalizeTreeShape(shape)];
  const factor = NATURAL_TOTAL_HEIGHT / (authored.trunkHeight + CANOPY_HEIGHT);
  return {
    trunkHeight: authored.trunkHeight * factor,
    trunkRadiusTop: TRUNK_RADIUS_TOP * factor,
    trunkRadiusBottom: TRUNK_RADIUS_BOTTOM * factor,
    canopyHeight: CANOPY_HEIGHT * factor,
    canopyRadius: CANOPY_RADIUS * factor,
    canopyYStretch: authored.canopyYStretch,
  };
}

function createHeightArrowGeometry() {
  const shaft = new THREE.CylinderGeometry(0.22, 0.22, 1.1, 16);
  shaft.translate(0, 0.55, 0);
  const head = new THREE.ConeGeometry(0.55, 1, 20);
  head.translate(0, 1.6, 0);
  const geometry = mergeGeometries([shaft, head], false);
  geometry.translate(0, -1.05, 0);
  return geometry;
}

export function createTreeMesh(shape = "circle") {
  const normalizedShape = normalizeTreeShape(shape);
  const dims = getTreeDimensions(normalizedShape);
  const tree = new THREE.Group();
  tree.name = `${normalizedShape === "cone" ? "ConeTree" : "RoundTopTree"}_${Date.now()}`;
  tree.userData.treeType = normalizedShape;

  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(dims.trunkRadiusTop, dims.trunkRadiusBottom, dims.trunkHeight, 8),
    new THREE.MeshStandardMaterial({ color: 0x6b4f2a, roughness: 0.82 })
  );
  trunk.position.y = dims.trunkHeight / 2;
  const foliage = normalizedShape === "circle"
    ? new THREE.Mesh(new THREE.SphereGeometry(dims.canopyRadius, 16, 16), new THREE.MeshStandardMaterial({ color: 0x2f7d32, roughness: 0.9 }))
    : new THREE.Mesh(new THREE.ConeGeometry(dims.canopyRadius, dims.canopyHeight, 16), new THREE.MeshStandardMaterial({ color: 0x2f7d32, roughness: 0.9 }));
  foliage.scale.y = normalizedShape === "circle" ? dims.canopyYStretch : 1;
  foliage.position.y = normalizedShape === "circle"
    ? dims.trunkHeight + dims.canopyRadius * dims.canopyYStretch
    : dims.trunkHeight + dims.canopyHeight / 2;
  const arrow = new THREE.Mesh(createHeightArrowGeometry(), new THREE.MeshStandardMaterial({ color: 0xff2200, emissive: 0x660000 }));
  arrow.name = "heightArrow";
  arrow.position.y = dims.trunkHeight + dims.canopyHeight + TREE_ARROW_OFFSET;
  arrow.visible = false;
  arrow.renderOrder = 999;

  tree.add(trunk, foliage, arrow);
  tree.userData.trunk = trunk;
  tree.userData.foliage = foliage;
  tree.userData.arrow = arrow;
  tree.userData.locked = false;
  tree.userData.groundZ = 0;
  tree.userData.__trunkBaseHeight = dims.trunkHeight;
  tree.userData.__foliageOffset = dims.canopyHeight / 2;
  tree.userData.__arrowOffset = TREE_ARROW_OFFSET;
  tree.userData.widthScale = 1;
  tree.userData.lengthScale = 1;
  tree.userData.heightScaleY = 1;
  tree.rotation.x = Math.PI / 2;
  return tree;
}

export function setTreeHeightScale(tree, heightScale) {
  const trunk = tree?.userData?.trunk;
  const foliage = tree?.userData?.foliage;
  const arrow = tree?.userData?.arrow;
  if (!trunk || !foliage) return;
  const h = Math.max(0.15, heightScale);
  const baseTrunkHeight = tree.userData.__trunkBaseHeight;
  const shape = normalizeTreeShape(tree.userData.treeType);
  const dims = getTreeDimensions(shape);
  const width = Math.max(0.15, tree.userData.widthScale ?? tree.scale.x ?? 1);
  const canopyHeight = dims.canopyHeight * Math.min(h, width);
  const trunkHeight = Math.max(0.3, NATURAL_TOTAL_HEIGHT * h - canopyHeight);
  tree.scale.set(width, 1, Math.max(0.15, tree.userData.lengthScale ?? width));
  trunk.scale.set(1, trunkHeight / baseTrunkHeight, 1);
  trunk.position.y = trunkHeight / 2;
  const baseFoliageY = shape === "circle" ? dims.canopyYStretch : 1;
  foliage.scale.set(1, baseFoliageY * Math.min(h, width), 1);
  foliage.position.y = trunkHeight + canopyHeight / 2;
  if (arrow) {
    const inverseWidth = 1 / width;
    arrow.scale.set(inverseWidth, 1, inverseWidth);
    arrow.position.y = trunkHeight + canopyHeight + TREE_ARROW_OFFSET;
  }
  tree.userData.heightScaleY = h;
  tree.updateMatrixWorld(true);
}
